import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, PolarAngleAxis, RadialBar, RadialBarChart, XAxis, YAxis } from 'recharts';

import type { ISegment, Segment } from '@feathr/blackbox';
import { SegmentStatHelper } from '@feathr/blackbox';
import { cssVar } from '@feathr/hooks';

interface IProps {
  segment: Segment;
  mode: 'count' | 'crumbs' | 'reachable' | 'emails';
  isCompact?: boolean;
}

function SegmentGauge({ segment, mode, isCompact = false }: IProps): JSX.Element {
  const [fullStatHelper] = React.useState(
    new SegmentStatHelper(
      observable<Partial<ISegment>>({
        lookback_mode: 'unbounded',
        lookback_value: 0,
        name: 'full segment',
        mode: 'match_any',
        predicates: [],
        account: '',
        is_custom: false,
      }),
    ),
  );
  const { t } = useTranslation();

  const count = segment[mode] ?? 0;
  const fullCount = fullStatHelper[mode];

  useEffect(() => {
    if (count > fullCount) {
      // Force @compute to recompute, due to a new array being assigned to observable.
      runInAction(() => {
        fullStatHelper.segment.predicates = [];
      });
    }
  }, [count, fullCount]);

  const data = [{ name: 'count', count, fill: cssVar('--color-primary-3') }];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {isCompact ? (
        <BarChart data={data} height={40} layout={'vertical'} width={350}>
          <Bar background={{ fill: 'rgb(238, 238, 238)' }} dataKey={'count'} />
          <YAxis axisLine={false} hide type={'category'} />
          <XAxis axisLine={false} domain={[0, fullCount]} hide type={'number'} />
        </BarChart>
      ) : (
        <RadialBarChart
          cy={125}
          data={data}
          endAngle={0}
          height={135}
          innerRadius={85}
          outerRadius={125}
          startAngle={180}
          width={250}
        >
          <PolarAngleAxis angleAxisId={0} domain={[0, fullCount]} tick={false} type={'number'} />
          <RadialBar background={true} dataKey={'count'} />
        </RadialBarChart>
      )}

      <label style={{ textAlign: 'center' }}>
        {(count || 0).toLocaleString()}/{(fullCount || 0).toLocaleString()}
        <br />
        {
          {
            count: t('People'),
            reachable: t('Reachable people'),
            crumbs: t('Breadcrumbs'),
            emails: t('Unique Email Addresses'),
          }[mode]
        }
      </label>
    </div>
  );
}

export default observer(SegmentGauge);
